import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Clock, FileText, Award, BookOpen, Trophy } from "lucide-react";
import { supabase } from "../lib/supabase";

interface Question {
  number: number;
  question: string;
  answer: string;
  solution: string;
  topic: string;
  difficulty: string;
}

interface SamplePaper {
  id: string;
  title: string;
  description: string;
  level: string;
  time_limit_minutes: number;
  total_questions: number;
  questions: Question[];
}

export default function SamplePapers() {
  const [papers, setPapers] = useState<SamplePaper[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState<
    "elementary" | "middle_school"
  >("elementary");
  const navigate = useNavigate();

  useEffect(() => {
    loadPapers();
  }, []);

  const loadPapers = async () => {
    try {
      const { data, error } = await supabase
        .from("sample_papers")
        .select("*")
        .order("created_at", { ascending: true });

      if (error) throw error;
      setPapers(data || []);
    } catch (error) {
      console.error("Failed to load sample papers:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPapers = papers.filter((p) => p.level === selectedLevel);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "easy":
        return "bg-green-100 text-green-700";
      case "medium":
        return "bg-yellow-100 text-yellow-700";
      case "hard":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const startPaper = (paperId: string) => {
    navigate(`/sample-paper/${paperId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading sample papers...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Trophy className="w-10 h-10 text-blue-600" />
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
            Math Olympiad Sample Papers
          </h1>
        </div>
        <p className="text-lg text-gray-600 dark:text-gray-400">
          Practice with MOEMS-style questions to prepare for competitions
        </p>
      </div>

      {/* Info Cards */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold">30 Minutes</p>
                <p className="text-sm text-gray-600">Per Paper</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="font-semibold">5 Questions</p>
                <p className="text-sm text-gray-600">Per Paper</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold">10 Papers</p>
                <p className="text-sm text-gray-600">5 Per Level</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Level Selection */}
      <Tabs
        value={selectedLevel}
        onValueChange={(v) =>
          setSelectedLevel(v as "elementary" | "middle_school")
        }
      >
        <TabsList className="mb-6">
          <TabsTrigger value="elementary">Elementary (Grades 4-6)</TabsTrigger>
          <TabsTrigger value="middle_school">
            Middle School (Grades 6-8)
          </TabsTrigger>
        </TabsList>

        <TabsContent value={selectedLevel}>
          {filteredPapers.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  No sample papers available for this level yet.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPapers.map((paper) => (
                <Card
                  key={paper.id}
                  className="hover:shadow-lg transition-shadow"
                >
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <CardTitle className="text-xl">{paper.title}</CardTitle>
                      <Badge
                        variant="outline"
                        className="bg-blue-50 text-blue-700"
                      >
                        {paper.total_questions} Questions
                      </Badge>
                    </div>
                    <CardDescription>{paper.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Time Info */}
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span>{paper.time_limit_minutes} minutes</span>
                      </div>

                      {/* Topics Covered */}
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-2">
                          Topics:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {[
                            ...new Set(paper.questions.map((q) => q.topic)),
                          ].map((topic) => (
                            <Badge
                              key={topic}
                              variant="secondary"
                              className="text-xs"
                            >
                              {topic}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Difficulty Distribution */}
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-2">
                          Difficulty:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {paper.questions.map((q) => (
                            <Badge
                              key={q.number}
                              className={`text-xs ${getDifficultyColor(q.difficulty)}`}
                            >
                              Q{q.number}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <Button
                        onClick={() => startPaper(paper.id)}
                        className="w-full"
                      >
                        Start Practice
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
